// API client for the FastAPI backend
// Routes all data access through fetch() to self-hosted endpoints

const API_BASE = process.env.REACT_APP_API_URL || '';
const MAX_ADMIN_UPLOAD_BYTES = 5 * 1024 * 1024;
const ADMIN_IMAGE_MAX_EDGE = 1600;
const ADMIN_IMAGE_MIN_EDGE = 800;
const ADMIN_IMAGE_QUALITY_STEPS = [0.82, 0.72, 0.62];

// =====================================================
// QUERY BUILDER SHIM (for RekoManager.js etc.)
// Translates api.from('table').select()... to fetch /api/reko/* calls
// =====================================================

const REKO_TABLE_ENDPOINTS = {
  reko_rings: '/api/reko/rings',
  reko_products: '/api/reko/products',
  reko_product_rings: '/api/reko/product-rings',
  reko_deliveries: '/api/reko/deliveries',
  reko_delivery_products: '/api/reko/delivery-products',
  reko_orders: '/api/reko/orders',
  reko_order_items: '/api/reko/order-items',
  reko_weekly_stock: '/api/reko/weekly-stock',
  reko_inventory: '/api/reko/inventory',
};

function createQueryBuilder(table) {
  const state = {
    table,
    method: 'GET',
    body: null,
    filters: [],
    orderCols: [],
    isSingle: false,
    selectFields: '*',
    inFilter: null,
  };

  const endpoint = REKO_TABLE_ENDPOINTS[table] || `/api/${table.replace(/_/g, '-')}`;

  const builder = {
    select(fields) {
      state.selectFields = fields || '*';
      // Only set GET if no write method already set (insert/update chain)
      if (!state.body && state.method === 'GET') {
        state.method = 'GET';
      }
      return builder;
    },
    insert(data) {
      state.method = 'POST';
      state.body = data;
      return builder;
    },
    update(data) {
      state.method = 'PATCH';
      state.body = data;
      return builder;
    },
    delete() {
      state.method = 'DELETE';
      return builder;
    },
    eq(col, val) {
      state.filters.push({ type: 'eq', col, val });
      return builder;
    },
    neq(col, val) {
      state.filters.push({ type: 'neq', col, val });
      return builder;
    },
    gt(col, val) {
      state.filters.push({ type: 'gt', col, val });
      return builder;
    },
    gte(col, val) {
      state.filters.push({ type: 'gte', col, val });
      return builder;
    },
    lt(col, val) {
      state.filters.push({ type: 'lt', col, val });
      return builder;
    },
    lte(col, val) {
      state.filters.push({ type: 'lte', col, val });
      return builder;
    },
    in(col, vals) {
      state.inFilter = { col, vals };
      return builder;
    },
    order(col, opts) {
      state.orderCols.push({ col, ...(opts || {}) });
      return builder;
    },
    single() {
      state.isSingle = true;
      return builder;
    },
    // Terminal: execute the query
    then(resolve, reject) {
      return executeQuery(endpoint, state).then(resolve, reject);
    },
  };

  return builder;
}

async function executeQuery(endpoint, state) {
  const token = getToken();
  const hdrs = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Build URL with query params
  const params = new URLSearchParams();
  for (const f of state.filters) {
    if (f.type === 'eq') {
      params.append(f.col, f.val);
    } else {
      // Send as _filter=col.op.val for gte/lte/gt/lt/neq
      params.append('_filter', `${f.col}.${f.type}.${f.val}`);
    }
  }
  if (state.inFilter) {
    params.append(state.inFilter.col, state.inFilter.vals.join(','));
    params.append('_in', state.inFilter.col);
  }
  if (state.orderCols.length) {
    params.append('_order', state.orderCols.map(o => `${o.col}${o.ascending === false ? '.desc' : ''}`).join(','));
  }
  if (state.selectFields && state.selectFields !== '*') {
    params.append('_select', state.selectFields);
  }

  let url = `${API_BASE}${endpoint}`;
  const qs = params.toString();
  if (qs && state.method === 'GET') url += `?${qs}`;

  try {
    let fetchUrl = url;
    let fetchOpts = { method: state.method, headers: hdrs };

    if (state.method === 'POST') {
      fetchOpts.body = JSON.stringify(state.body);
    } else if (state.method === 'PATCH') {
      // For update with eq filter, append id to URL
      const idFilter = state.filters.find(f => f.col === 'id');
      if (idFilter) {
        fetchUrl = `${API_BASE}${endpoint}/${idFilter.val}`;
      }
      fetchOpts.body = JSON.stringify(state.body);
    } else if (state.method === 'DELETE') {
      const idFilter = state.filters.find(f => f.col === 'id');
      if (idFilter) {
        fetchUrl = `${API_BASE}${endpoint}/${idFilter.val}`;
      } else {
        // DELETE with non-id filters: pass as query params
        if (qs) fetchUrl += `?${qs}`;
      }
    }

    const res = await fetch(fetchUrl, fetchOpts);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      return { data: null, error: new Error(extractApiErrorMessage(err, res.statusText || `HTTP ${res.status}`)) };
    }

    const data = await res.json();

    if (state.isSingle) {
      const row = Array.isArray(data) ? data[0] : data;
      return { data: row || null, error: null };
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

export const apiClient = {
  from: (table) => createQueryBuilder(table),
  auth: {
    getUser: async () => {
      const user = await getCurrentUser();
      return { data: { user }, error: null };
    },
    getSession: async () => {
      const session = await getSession();
      return { data: { session }, error: null };
    },
    signInWithPassword: async ({ email, password }) => {
      const result = await signIn(email, password);
      return { data: result, error: null };
    },
    signOut: async () => {
      await signOut();
      return { error: null };
    },
    onAuthStateChange: (callback) => {
      // No-op for now — auth state changes handled by component
      return { data: { subscription: { unsubscribe: () => {} } } };
    },
  },
};

// =====================================================
// HELPERS
// =====================================================

const getToken = () => localStorage.getItem('admin_token');

const headers = (extra = {}) => ({
  'Content-Type': 'application/json',
  ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
  ...extra,
});

const extractApiErrorMessage = (payload, fallback) => {
  if (typeof payload === 'string') return payload;
  if (payload?.error) return payload.error;
  if (payload?.detail) return typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail);
  if (payload?.message) return payload.message;
  return fallback;
};

const apiRaw = async (path, options = {}) => {
  const res = await fetch(`${API_BASE}${path}`, { headers: headers(), ...options });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(extractApiErrorMessage(err, res.statusText || `HTTP ${res.status}`));
  }
  return res.json();
};

const api = async (path, options = {}) => {
  try {
    const data = await apiRaw(path, options);
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

const get = (path) => api(path);
const post = (path, body) => api(path, { method: 'POST', body: JSON.stringify(body) });
const put = (path, body) => api(path, { method: 'PUT', body: JSON.stringify(body) });
const patch = (path, body) => api(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = (path) => api(path, { method: 'DELETE' });

const canResizeImageInBrowser = (file) => (
  file?.type?.startsWith('image/')
  && file.type !== 'image/gif'
  && typeof Image !== 'undefined'
  && typeof document !== 'undefined'
  && typeof URL !== 'undefined'
  && typeof URL.createObjectURL === 'function'
);

const loadBrowserImage = (file) => new Promise((resolve, reject) => {
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(imageUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(imageUrl);
    reject(new Error('Kunde inte läsa bilden för automatisk minskning.'));
  };
  image.src = imageUrl;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
      return;
    }
    reject(new Error('Kunde inte minska bilden automatiskt.'));
  }, type, quality);
});

const resizedFileName = (name) => `${(name || 'bild').replace(/\.[^.]+$/, '')}.webp`;

export const prepareAdminUploadImage = async (file) => {
  if (!file || file.size <= MAX_ADMIN_UPLOAD_BYTES) return file;
  if (!canResizeImageInBrowser(file)) {
    throw new Error('Bilden är för stor (max 5 MB) och kunde inte minskas automatiskt i den här webbläsaren.');
  }

  const image = await loadBrowserImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  let maxEdge = ADMIN_IMAGE_MAX_EDGE;
  let bestBlob = null;

  while (maxEdge >= ADMIN_IMAGE_MIN_EDGE) {
    const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(image, 0, 0, width, height);

    for (const quality of ADMIN_IMAGE_QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, 'image/webp', quality);
      bestBlob = blob;
      if (blob.size <= MAX_ADMIN_UPLOAD_BYTES) {
        return new File([blob], resizedFileName(file.name), {
          type: 'image/webp',
          lastModified: Date.now(),
        });
      }
    }
    maxEdge = Math.floor(maxEdge * 0.75);
  }

  if (bestBlob) {
    throw new Error('Bilden är fortfarande för stor efter automatisk minskning. Beskär den eller välj en mindre bild.');
  }
  throw new Error('Kunde inte minska bilden automatiskt.');
};

export const uploadAdminImage = async (file) => {
  const uploadFile = await prepareAdminUploadImage(file);
  const form = new FormData();
  form.append('file', uploadFile);
  const token = getToken();
  const res = await fetch(`${API_BASE}/api/admin/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.detail || err.error || res.statusText);
  }
  return res.json();
};

// =====================================================
// AUTH
// =====================================================

export const signIn = async (email, password) => {
  const result = await apiRaw('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  const token = result.access_token || result.token;
  localStorage.setItem('admin_token', token);
  return { user: result.data?.user || result.user, session: { access_token: token } };
};

export const signOut = async () => {
  localStorage.removeItem('admin_token');
};

export const getCurrentUser = async () => {
  const token = getToken();
  if (!token) return null;
  try {
    const result = await apiRaw('/api/auth/me');
    return result.data?.user || result.user;
  } catch {
    return null;
  }
};

// Invite new user (admin-only)
export const inviteUser = async (name, email, password) => {
  return apiRaw('/api/auth/invite', {
    method: 'POST',
    body: JSON.stringify({ name, email, password }),
  });
};

// List all users (admin-only)
export const getUsers = async () => {
  return apiRaw('/api/auth/users');
};

// Delete user (admin-only)
export const deleteUser = async (id) => {
  return apiRaw(`/api/auth/users/${id}`, { method: 'DELETE' });
};

export const getSession = async () => {
  const token = getToken();
  if (!token) return null;
  return { access_token: token };
};

// =====================================================
// CONTACTS
// =====================================================

export const submitContactForm = async (formData) => {
  return post('/api/contact', {
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    subject: formData.subject,
    message: formData.message,
  });
};

export const getContacts = () => get('/api/contacts');
export const updateContact = (id, updates) => put(`/api/contacts/${id}`, updates);
export const deleteContact = (id) => del(`/api/contacts/${id}`);

// =====================================================
// CMS / SITE CONTENT
// =====================================================

export const getSiteContent = () => get('/api/site-content');
export const getSiteContentBySection = (section) => get(`/api/site-content/${section}`);

const normalizeSiteContentUpdates = (updatesOrField, value) => (
  typeof updatesOrField === 'string' ? { [updatesOrField]: value } : updatesOrField
);

const stripSiteContentMeta = (content) => {
  if (!content || typeof content !== 'object') return {};
  const { id, key, section, updated_at, ...fields } = content;
  return fields;
};

const siteContentPayload = (updates) => ({
  value: JSON.stringify(updates || {}),
});

const isNotFoundError = (error) => {
  const message = error?.message || '';
  return message.includes('404') || message.toLowerCase().includes('not found');
};

export const createSiteContent = (section, updatesOrField, value) => (
  post(`/api/site-content/${section}`, siteContentPayload(normalizeSiteContentUpdates(updatesOrField, value)))
);

export const saveSiteContent = async (section, updatesOrField, value) => {
  const updates = normalizeSiteContentUpdates(updatesOrField, value);
  const existing = await getSiteContentBySection(section);
  const nextContent = existing.error
    ? updates
    : { ...stripSiteContentMeta(existing.data), ...updates };

  const updated = await patch(`/api/site-content/${section}`, siteContentPayload(nextContent));
  if (!updated.error) return updated;

  if (!isNotFoundError(updated.error)) return updated;

  return createSiteContent(section, updates);
};

export const updateSiteContent = saveSiteContent;

export const bulkUpdateSiteContent = async (updates) => {
  return Promise.all(updates.map(({ section, ...data }) => saveSiteContent(section, data)));
};

// =====================================================
// RESTAURANT OPERATIONS
// =====================================================

export const getRestaurantOperations = () => get('/api/operations');
export const updateRestaurantOperation = (id, updates) => put(`/api/operations/${id}`, updates);
export const deleteRestaurantOperation = (id) => del(`/api/operations/${id}`);
export const addRestaurantOperation = (op) => post('/api/operations', op);
export const setRestaurantOperation = (op) => addRestaurantOperation(op);
export const updateRestaurantOperationAddress = (id, address) => updateRestaurantOperation(id, { address });

// =====================================================
// DAILY SALES
// =====================================================

export const getDailySales = () => get('/api/operations/daily-sales');
export const getDailyMealSales = () => get('/api/operations/daily-meal-sales');
export const createOrUpdateDailySale = (data) => post('/api/operations/daily-sales', data);
export const createOrUpdateDailyMealSale = (data) => post('/api/operations/daily-meal-sales', data);
export const setSalesForDate = (date, sales) => createOrUpdateDailySale({ date, ...sales });
export const setMealSalesForDate = (date, sales) => createOrUpdateDailyMealSale({ date, ...sales });

// =====================================================
// INGREDIENTS
// =====================================================

export const getIngredients = () => get('/api/ingredients');
export const searchSnabbgrossProducts = (query) => {
  const normalized = typeof query === 'string' ? query.trim() : '';
  if (normalized.length < 2 || normalized.length > 100) {
    return Promise.reject(new Error('Ange minst två tecken för Snabbgross-sökningen.'));
  }
  return apiRaw(`/api/admin/suppliers/snabbgross/products/search?q=${encodeURIComponent(normalized)}`);
};
export const linkIngredientToSnabbgross = (ingredientId, productId, confirmReplace = false) =>
  apiRaw(`/api/admin/ingredients/${encodeURIComponent(ingredientId)}/snabbgross`, {
    method: 'POST',
    body: JSON.stringify({ product_id: productId, confirm_replace: Boolean(confirmReplace) }),
  });
export const syncIngredientSnabbgross = ingredientId =>
  apiRaw(`/api/admin/ingredients/${encodeURIComponent(ingredientId)}/snabbgross/sync`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const addIngredient = async (ingredient) => {
  const result = await post('/api/ingredients', ingredient);
  if (result.error) return result;
  return { data: result.data?.data ?? result.data, error: null };
};
export const updateIngredient = (id, updates) => put(`/api/ingredients/${id}`, updates);
export const deleteIngredient = (id) => del(`/api/ingredients/${id}`);

// =====================================================
// RECIPES
// =====================================================

export const getRecipes = () => get('/api/recipes');
export const addRecipe = async (recipe) => {
  const result = await post('/api/recipes', recipe);
  if (result.error) return result;
  return { data: result.data?.recipe ?? result.data, error: null };
};
export const updateRecipe = (id, updates) => put(`/api/recipes/${id}`, updates);
export const deleteRecipe = (id) => del(`/api/recipes/${id}`);
export const getIntermediateRecipes = () => get('/api/recipes/intermediates');
export const getRecipeSubRecipes = (recipeId) => get(`/api/recipes/${recipeId}/sub-recipes`);
export const addRecipeSubRecipe = (recipeId, data) => post(`/api/recipes/${recipeId}/sub-recipes`, data);
export const removeRecipeSubRecipe = (recipeId, subRecipeId) => del(`/api/recipes/${recipeId}/sub-recipes/${subRecipeId}`);
export const getAuthoritativeRecipeCost = (recipeId) => get(`/api/admin/costs/recipes/${encodeURIComponent(recipeId)}`);
export const getAuthoritativeMealCost = (mealId) => get(`/api/admin/costs/meals/${encodeURIComponent(mealId)}`);

// =====================================================
// MEALS
// =====================================================

export const getMeals = () => get('/api/meals');
export const addMeal = (meal) => post('/api/meals', meal);
export const updateMeal = (id, updates) => put(`/api/meals/${id}`, updates);
export const deleteMeal = (id) => del(`/api/meals/${id}`);

// =====================================================
// RECIPE INGREDIENTS & MEAL RECIPES
// =====================================================

export const getRecipeIngredients = async () => ({ data: [], error: null }); // Use /api/recipes/:id/ingredients instead
export const getMealRecipes = async () => ({ data: [], error: null }); // Use /api/meals/:id/recipes instead

export const addRecipeIngredient = (recipeId, relation) =>
  post(`/api/recipes/${recipeId}/ingredients`, relation);
export const updateRecipeIngredient = (recipeId, ingredientId, updates) =>
  put(`/api/recipes/${recipeId}/ingredients/${ingredientId}`, updates);
export const deleteRecipeIngredient = (recipeId, ingredientId) => del(`/api/recipes/${recipeId}/ingredients/${ingredientId}`);
export const removeRecipeIngredient = (recipeId, ingredientId) => deleteRecipeIngredient(recipeId, ingredientId);

export const addMealRecipe = (mealId, recipeId, servings) =>
  post(`/api/meals/${mealId}/recipes`, { recipe_id: recipeId, quantity: servings });
export const updateMealRecipe = (mealId, recipeId, servings) =>
  put(`/api/meals/${mealId}/recipes/${recipeId}`, { quantity: servings });
export const deleteMealRecipe = (mealId, recipeId) => del(`/api/meals/${mealId}/recipes/${recipeId}`);
export const removeMealRecipe = (mealId, recipeId) => deleteMealRecipe(mealId, recipeId);

// =====================================================
// OPENING HOURS
// =====================================================

export const getSeasonalHours = () => get('/api/settings/hours/seasonal');
export const addSeasonalHours = (hours) => post('/api/settings/hours/seasonal', hours);
export const updateSeasonalHours = (id, updates) => put(`/api/settings/hours/seasonal/${id}`, updates);
export const deleteSeasonalHours = (id) => del(`/api/settings/hours/seasonal/${id}`);
export const createSeasonalHours = (hours) => addSeasonalHours(hours);
export const getSeasonalHoursWithSchedule = (id) => get(`/api/settings/hours/seasonal/${id}`);
export const setWeeklySchedule = (seasonId, schedule) => put(`/api/settings/hours/seasonal/${seasonId}/weekly`, schedule);
export const saveWeeklySchedule = setWeeklySchedule;
export const getOpeningHourExceptions = () => get('/api/settings/hours/exceptions');
export const createOpeningHourException = (exception) => post('/api/settings/hours/exceptions', exception);
export const updateOpeningHourException = (id, exception) => put(`/api/settings/hours/exceptions/${id}`, exception);
export const deleteOpeningHourException = (id) => del(`/api/settings/hours/exceptions/${id}`);
export const getOpeningHoursRange = (start, end, location) => {
  const params = new URLSearchParams({ start, end });
  if (location) params.set('location', location);
  return get(`/api/opening-hours/resolve?${params.toString()}`);
};

// =====================================================
// SYSTEM SETTINGS
// =====================================================

export const getSystemSettings = () => get('/api/settings');
export const updateSystemSettings = (settings) => put('/api/settings', settings);

// /api/settings returnerar en lista med rader ({ key, value } eller
// { setting_key, setting_value }) — slå upp ett enskilt värde ur den.
export const getSystemSettingValue = async (key) => {
  const { data } = await getSystemSettings();
  const row = Array.isArray(data)
    ? data.find((setting) => (setting.setting_key || setting.key) === key)
    : null;
  const value = row?.setting_value ?? row?.value ?? data?.[key];
  return typeof value === 'string' ? value.replace(/^"|"$/g, '') : value;
};

// KPI-mål är affärsvärden och får därför bara komma från system_settings.
// Saknas en nyckel returneras null — aldrig ett inbyggt riktvärde.
const parseSettingNumber = (raw) => {
  if (raw == null || raw === '') return null;
  const value = Number(String(raw).replace(/^"|"$/g, '').replace(',', '.'));
  return Number.isFinite(value) ? value : null;
};

export const getKpiTargets = async (keys) => {
  const { data } = await getSystemSettings();
  const rows = Array.isArray(data) ? data : [];
  const byKey = new Map(rows.map((setting) => [
    setting.setting_key || setting.key,
    setting.setting_value ?? setting.value,
  ]));
  return keys.reduce((targets, key) => {
    targets[key] = parseSettingNumber(byKey.has(key) ? byKey.get(key) : data?.[key]);
    return targets;
  }, {});
};

// Tomt fält betyder "inget mål" och skrivs som tom sträng, så att ägaren kan ta
// bort ett mål utan att det tolkas som noll.
export const saveKpiTargets = (targets) => updateSystemSettings(
  Object.entries(targets).reduce((payload, [key, value]) => {
    payload[key] = value == null || value === '' ? '' : String(value);
    return payload;
  }, {}),
);

// =====================================================
// SHOP PRODUCTS
// =====================================================

export const getAvailableProducts = async () => (await get('/api/products')).data || [];
export const getAllProducts = async () => (await get('/api/products/all')).data || [];
export const createProduct = (productData) => post('/api/products', productData);
export const updateProduct = (id, updates) => put(`/api/products/${id}`, updates);
export const deleteProduct = (id) => del(`/api/products/${id}`);

// =====================================================
// SHOP
// =====================================================

export const getShopSettings = async () => (await get('/api/shop/settings')).data || null;
export const updateShopSettings = (settings) => put('/api/shop/settings', settings);
export const getAdminShopProducts = async () => (await get('/api/admin/shop/products')).data || [];
export const createAdminShopProduct = async (product) => {
  const response = (await post('/api/admin/shop/products', product)).data;
  return response?.data || response || null;
};
export const updateAdminShopProduct = async (id, updates) => {
  const response = (await patch(`/api/admin/shop/products/${id}`, updates)).data;
  return response?.data || response || null;
};
export const deleteAdminShopProduct = (id) => del(`/api/admin/shop/products/${id}`);
export const createAdminShopVariant = async (productId, variant) => {
  const response = (await post(`/api/admin/shop/products/${productId}/variants`, variant)).data;
  return response?.data || response || null;
};
export const updateAdminShopVariant = async (id, updates) => {
  const response = (await patch(`/api/admin/shop/variants/${id}`, updates)).data;
  return response?.data || response || null;
};
export const deleteAdminShopVariant = (id) => del(`/api/admin/shop/variants/${id}`);
export const getAdminShopOrders = async () => (await get('/api/admin/shop/orders')).data || [];
export const updateAdminShopOrder = async (id, updates) => {
  const response = (await patch(`/api/admin/shop/orders/${id}`, updates)).data;
  return response?.data || response || null;
};

// =====================================================
// PICKUP DATES
// =====================================================

export const getAvailablePickupDates = async () => (await get('/api/orders/pickup-dates')).data || [];
export const getAllPickupDates = async () => (await get('/api/orders/pickup-dates/all')).data || [];
export const createPickupDate = (dateData) => post('/api/orders/pickup-dates', dateData);
export const updatePickupDate = (id, updates) => put(`/api/orders/pickup-dates/${id}`, updates);
export const deletePickupDate = (id) => del(`/api/orders/pickup-dates/${id}`);

// =====================================================
// ORDER FORM CONTENT
// =====================================================

export const getOrderFormContent = async () => {
  try { const { data } = await get('/api/orders/form-content'); return data || { header_text: 'Beställ dina produkter här!', footer_text: 'Vi ser fram emot din beställning!', min_pickup_days: 7, max_pickup_date: null, form_enabled: true }; }
  catch { return { header_text: 'Beställ dina produkter här!', footer_text: 'Vi ser fram emot din beställning!', min_pickup_days: 7, max_pickup_date: null, form_enabled: true }; }
};
export const updateOrderFormContent = (updates) => put('/api/orders/form-content', updates);

// =====================================================
// PRODUCT ORDERS
// =====================================================

export const getBookingOptions = async () => {
  const { data, error } = await get('/api/orders/booking-options');
  if (error) throw error;
  return data;
};

export const submitProductOrder = async (orderData) => {
  return post('/api/orders', {
    name: orderData.name,
    email: orderData.email,
    phone: orderData.phone,
    fulfillment_method: orderData.fulfillment_method,
    pickup_date: orderData.pickup_date,
    delivery_street: orderData.delivery_street,
    delivery_postal_code: orderData.delivery_postal_code,
    delivery_city: orderData.delivery_city,
    delivery_note: orderData.delivery_note,
    items: orderData.items,
    notes: orderData.notes,
    request_id: orderData.request_id,
  });
};

export const getDeliveryQuote = (address) => post('/api/orders/delivery-quote', address);

export const getProductOrders = async () => (await get('/api/orders')).data || [];
export const updateProductOrder = (id, updates) => put(`/api/orders/${id}`, updates);
export const deleteProductOrder = (id) => del(`/api/orders/${id}`);
export const createAdminProductOrder = (orderData) => post('/api/admin/product-orders', orderData);
export const updateAdminProductOrder = (id, orderData) => put(`/api/orders/${id}`, orderData);

// Vikt (kg) för en viktvara, fylls i vid packning – backend räknar om rad och total.
export const setOrderItemWeight = (orderId, itemId, weightKg) =>
  put(`/api/orders/${orderId}/items/${itemId}/weight`, { weight_kg: weightKg });

export const sendOrderReadyEmail = (order) => post(`/api/orders/${order.id}/send-ready-email`, {});

export const getWeeklyProductSummary = async () => (await get('/api/orders/weekly-summary')).data || [];
export const getOrderPickList = async () => (await get('/api/orders/pick-list')).data || [];

// =====================================================
// PRICE HISTORY & ALERTS
// =====================================================

export const getPriceHistory = (entityType, entityId, days = 90) =>
  get(`/api/price-history/${entityType}/${entityId}?days=${days}`);

export const getPriceAlerts = () => get('/api/price-history/alerts');

// =====================================================
// PAGE CONTENT
// =====================================================

export const getPageContent = async (pageName) => {
  try { return { data: await get(`/api/page-content/${pageName}`) }; }
  catch { return { data: null }; }
};

export const savePageContent = async (pageName, content) => {
  try { await put(`/api/page-content/${pageName}`, { content }); return { error: null }; }
  catch (e) { return { error: e.message }; }
};

// =====================================================
// SPECIAL EVENTS
// =====================================================

// (accessed via generic CRUD on backend)
// Add if needed: get/post/put/delete /api/special-events

// =====================================================
// WEATHER (stubs)
// =====================================================

export const getWeatherData = async () => [];
export const getWeatherAddress = async () => null;
export const updateWeatherAddress = async (address) => ({ address });
export const getWeatherCollectionStatus = async () => ({ data: { enabled: false, lastCollection: null }, error: null });
export const getWeatherForecastData = async () => null;
export const getWeatherInsightsRange = async () => [];

// =====================================================
// SETTINGS HELPERS — läser från system_settings via API
// =====================================================

const _settingHelper = (key) => async () => {
  const val = await getSystemSettingValue(key);
  if (val == null) throw new Error(`system_settings-nyckel '${key}' saknas`);
  return { data: parseFloat(val), error: null };
};

export const getVatRateFood      = _settingHelper('vat_rate_food');
export const getVatRateDrinks    = _settingHelper('vat_rate_drinks');
export const getVatRateBeverages = _settingHelper('vat_rate_beverages');
export const getVatRateAlcohol   = _settingHelper('vat_rate_alcohol');
export const getHourlyLaborCost  = _settingHelper('hourly_labor_cost');
export const getRentPercentage   = _settingHelper('rent_percentage');

export const getVatTypes = async () => [
  { value: 'food', label: 'Mat (12%)', rate: 12 },
  { value: 'catering', label: 'Catering (12%)', rate: 12 },
  { value: 'alcohol', label: 'Alkohol (25%)', rate: 25 },
  { value: 'custom', label: 'Anpassad', rate: null },
];
export const updateVatType = async (id, rate) => ({ id, rate });
export const updateLaborCost = async (cost) => ({ cost });
export const updateRentPercentage = async (percentage) => ({ percentage });
export const getRecommendationBuffer = async () => 1.2;
export const updateRecommendationBuffer = async (buffer) => ({ buffer });
export const getHighTrendMultiplier = async () => 1.2;
export const updateHighTrendMultiplier = async (multiplier) => ({ multiplier });
export const getLowTrendMultiplier = async () => 0.8;
export const updateLowTrendMultiplier = async (multiplier) => ({ multiplier });
export const getHighRevenueThresholdMultiplier = async () => 1.2;
export const updateHighRevenueThreshold = async (threshold) => ({ threshold });
export const getBulkAnalytics = async () => [];
export const getBulkMealSales = async () => [];

const vassenAnalyticsRows = (payload) => {
  const ownerRows = payload?.report?.ownerDaily || [];
  const sourceRows = ownerRows.length
    ? ownerRows.map((day) => ({ ...day, source_detail: 'owner_report' }))
    : (payload?.aggregate?.daily || []).map((day) => ({
      ...day,
      totalSalesExVat: day.exVat,
      totalUnits: day.units,
      source_detail: 'aggregate',
    }));

  return sourceRows.map((day) => ({
    operation_date: day.date,
    is_open: Number(day.totalSalesExVat || 0) > 0,
    total_revenue: Number(day.totalSalesExVat || 0),
    // Heynow saknar råvaru- och hyreskostnad per Vassen-dag. Null är
    // avsiktligt: Analytics får aldrig presentera noll som ett verifierat facit.
    total_cost: null,
    labor_cost: day.labor?.allocatedLaborCost ?? null,
    rent_cost: null,
    customer_count: Number(day.decisionUnits || 0),
    customer_count_is_proxy: true,
    hours_worked: day.labor?.paidHours ?? null,
    scheduled_open_hours: day.openingHours?.expectedHours ?? null,
    weather_description: day.weather?.dominantWeatherCode == null
      ? ''
      : `Väderkod ${day.weather.dominantWeatherCode}`,
    temperature: day.weather?.avgTempC ?? null,
    has_complete_cost_data: false,
    has_labor_data: Boolean(day.labor),
    source_detail: day.source_detail,
  }));
};

export const getCachedAnalytics = async (startDate, endDate) => {
  try {
    const payload = await apiRaw('/api/admin/vassen-reports');
    const data = vassenAnalyticsRows(payload).filter((day) => (
      (!startDate || day.operation_date >= startDate)
      && (!endDate || day.operation_date <= endDate)
    ));
    return { data, error: null, cached: false, source: 'vassen_heynow_live' };
  } catch (error) {
    return { data: [], error, cached: false, source: 'vassen_heynow_live' };
  }
};
export const getForecastData = async () => null;
export const getMLForecastData = async () => null;
export const getConfig = async () => ({});
export const getDailyWeightedVatRate = async () => 0.12;
export const getDailyStats = async () => null;
export const getDailyIngredientCost = async () => 0;
export const saveDailyStats = async (date, stats) => ({ date, stats });
// =====================================================
// PACKAGING COSTS
// =====================================================

export const getPackagingCosts = () => get('/api/packaging-costs');
export const addPackagingCost = (data) => post('/api/packaging-costs', data);
export const updatePackagingCost = (id, updates) => put(`/api/packaging-costs/${id}`, updates);
export const deletePackagingCost = (id) => del(`/api/packaging-costs/${id}`);

// =====================================================
// RECIPE PACKAGING
// =====================================================

export const getRecipePackaging = (recipeId) => get(`/api/recipes/${recipeId}/packaging`);
export const addRecipePackaging = (recipeId, data) => post(`/api/recipes/${recipeId}/packaging`, data);
export const deleteRecipePackaging = (recipeId, packagingId) => del(`/api/recipes/${recipeId}/packaging/${packagingId}`);

// =====================================================
// GROSS COST / PRICING
// =====================================================

export const getGrossCost = (recipeId) => get(`/api/costs/gross/${recipeId}`);
export const getCostPricing = (recipeId) => get(`/api/costs/pricing/${recipeId}`);
export const calculateTransportCost = (data) => post('/api/costs/transport', data);
export const getPricingStrategy = (recipeId) => get(`/api/pricing/${recipeId}`);
export const savePricingStrategy = (data) => post('/api/pricing', data);
export const updatePricingStrategy = (id, updates) => put(`/api/pricing/${id}`, updates);

// =====================================================
// CATERING QUOTES
// =====================================================

// --- Publik cateringoffert (offertförfrågningar) ---
export const getCateringPackages = async () => (await get('/api/catering/packages')).data || [];
export const getCateringEventTypes = async () => (await get('/api/catering/event-types')).data || [];
export const calculateCateringPrice = (payload) => post('/api/catering/calculate', payload);
export const submitCateringRequest = (payload, signal) => api('/api/catering/requests', {
  method: 'POST',
  body: JSON.stringify(payload),
  signal,
});
// Admin
export const adminListCateringPackages = async () => (await get('/api/catering/packages/all')).data || [];
export const createCateringPackage = (data) => post('/api/catering/packages', data);
export const updateCateringPackage = (id, updates) => put(`/api/catering/packages/${id}`, updates);
export const deleteCateringPackage = (id) => del(`/api/catering/packages/${id}`);
export const listCateringRequests = async () => (await get('/api/admin/catering-requests')).data || [];
export const updateCateringRequest = (id, updates) => put(`/api/admin/catering-requests/${id}`, updates);
export const updateCateringEventTypes = (eventTyper) => put('/api/catering/event-types', { event_typer: eventTyper });

export const getCateringQuotes = () => get('/api/catering/quotes');
export const getCateringQuote = (id) => get(`/api/catering/quotes/${id}`);
export const createCateringQuote = (data) => post('/api/catering/quotes', data);
export const updateCateringQuote = (id, updates) => put(`/api/catering/quotes/${id}`, updates);
export const deleteCateringQuote = (id) => del(`/api/catering/quotes/${id}`);
export const getCateringQuotePdf = (id) => get(`/api/catering/quotes/${id}/pdf`);

// =====================================================
// VASSEN UPPFÖLJNING - bulkuppladdning av Heynow-rapporter
// =====================================================

const postVassenReportFiles = async (path, files, replaceDates) => {
  const form = new FormData();
  Array.from(files).forEach((file) => form.append('files', file, file.name));
  if (replaceDates?.length) form.append('replace_dates', replaceDates.join(','));
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(extractApiErrorMessage(err, res.statusText || `HTTP ${res.status}`));
  }
  return res.json();
};

export const getVassenReports = () => apiRaw('/api/admin/vassen-reports', { cache: 'no-store' });
export const previewVassenReports = (files) => postVassenReportFiles('/api/admin/vassen-reports/preview', files);
export const uploadVassenReports = (files, replaceDates = []) => postVassenReportFiles('/api/admin/vassen-reports', files, replaceDates);
export const deleteVassenReport = (reportDate) => apiRaw(`/api/admin/vassen-reports/${reportDate}`, { method: 'DELETE' });

// =====================================================
// SIE4 - normaliserat bokföringsunderlag för ägarrapporten
// =====================================================

const postSieFile = async (path, file, confirmedSourceSha256 = null, previewReceipt = null) => {
  const form = new FormData();
  form.append('file', file, file.name);
  if (confirmedSourceSha256) form.append('confirmed_source_sha256', confirmedSourceSha256);
  if (previewReceipt) form.append('preview_receipt', previewReceipt);
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(extractApiErrorMessage(err, res.statusText || `HTTP ${res.status}`));
  }
  return res.json();
};

export const getSieImports = () => apiRaw('/api/admin/sie-imports', { cache: 'no-store' });
export const previewSieImport = (file) => postSieFile('/api/admin/sie-imports/preview', file);
export const importSieFile = (file, confirmedSourceSha256, previewReceipt) => (
  postSieFile('/api/admin/sie-imports', file, confirmedSourceSha256, previewReceipt)
);

// =====================================================
// CHARCUTERIE
// =====================================================

export const getCharcuterieOverview = () => get('/api/charcuterie/overview');
export const calculateCharcuterieBatch = (data) => post('/api/charcuterie/batch', data);
export const getCharcuterieMargins = () => get('/api/charcuterie/margins');

// =====================================================
// UTILITY FUNCTIONS (kept as-is)
// =====================================================

export const formatDateSafe = (date) => {
  if (!date) return '';
  try { return new Date(date).toLocaleDateString('sv-SE'); }
  catch { return ''; }
};

export const calculatePriceExVat = (priceInclVat, vatRate) => priceInclVat / (1 + vatRate);
export const calculatePriceInclVat = (priceExVat, vatRate) => priceExVat * (1 + vatRate);
export const calculateVatAmount = (priceInclVat, vatRate) => priceInclVat - calculatePriceExVat(priceInclVat, vatRate);

export const errorHandler = {
  handle: (error, context) => { console.error(`Error in ${context}:`, error); },
};

// =====================================================
// WHOLESALE SUPPLIERS
// =====================================================

export const getSuppliers = () => get('/api/admin/suppliers');
export const addSupplier = (supplier) => post('/api/admin/suppliers', supplier);
export const updateSupplier = (id, data) => put(`/api/admin/suppliers/${id}`, data);
export const deleteSupplier = (id) => del(`/api/admin/suppliers/${id}`);

export const testSupplierConnection = async (id) => {
  return apiRaw(`/api/admin/suppliers/${id}/test`, { method: 'POST' });
};

export const searchAllWholesalers = async (query) => {
  return apiRaw(`/api/wholesale/search/${encodeURIComponent(query)}`);
};

// =====================================================
// EVENTS (kalender — caterings, marknader, evenemang)
// =====================================================

export const getEvents = (range) => {
  const qs = range?.start && range?.end
    ? `?start=${encodeURIComponent(range.start)}&end=${encodeURIComponent(range.end)}`
    : '';
  return get(`/api/events${qs}`);
};
export const createEvent = (event) => post('/api/events', event);
export const updateEvent = (id, updates) => put(`/api/events/${id}`, updates);
export const deleteEvent = (id) => del(`/api/events/${id}`);
